import { graphql } from 'client/gql';
import { BpSubpage } from 'components/BpSubpage/BpSubpage';
import { ClassbookAbsencesModal } from 'components/Classbook/modals/ClassbookAbsencesModal';
import { ClassbookAdministrationTable } from 'components/Classbook/tables/ClassbookAdministrationTable';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useConfirm } from 'hooks/useConfirm';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showErrorToast } from 'utils/showErrorToast';
import { showSuccessToast } from 'utils/showSuccessToast';

const DeleteAbsenceMutationDocument = graphql(`
  mutation BpDeleteAbsenceFromClassbookAdmin($where: AbsenceWhere!) {
    deleteAbsences(where: $where) {
      nodesDeleted
    }
  }
`);

const ClassbookAdministrationSubpagePageDataDocument = graphql(`
  query ClassbookAdministrationSubpagePageData(
    $profilesWhere: ProfileWhere!
    $classesWhere: ClassWhere!
    $profile: ID!
  ) {
    ...PermissionCheckerFields
    ...ClassbookAdministrationTableFields
  }
`);

export const ClassbookAdministrationSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();

  const [selectedAbsenceUuid, setSelectedAbsenceUuid] = useState<string | null>(null);
  const [absencesModalOpen, setAbsencesModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [, deleteAbsence] = useMutation(DeleteAbsenceMutationDocument);

  const [{ data }] = useQuery({
    query: ClassbookAdministrationSubpagePageDataDocument,
    variables: {
      profile: pimAuthClaims.getProfile().uuid,
      profilesWhere: {
        organization: {
          uuid: pimAuthClaims.getOrganizationUuid(),
        },
      },
      classesWhere: {
        organization: {
          uuid: pimAuthClaims.getOrganizationUuid(),
        },
      },
    },
  });

  const permissions = usePermissionChecker(data);

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', { type: t('absences.title', { count: 1 }), context: 'female' }),
    defaultConfirmText: t('delete.delete'),
  });

  async function handleDelete(uuid: string) {
    setLoading(true);
    const result = await deleteAbsence({ where: { uuid } });
    setLoading(false);
    if (result.error) {
      showErrorToast(result.error);
    } else {
      showSuccessToast(t('absences.absenceDeleted'));
    }
    setSelectedAbsenceUuid(null);
  }

  return (
    <BpSubpage isForbidden={!permissions?.canViewClassbookAdministration(pimAuthClaims.getOrganization())}>
      <ClassbookAdministrationTable
        isLoading={loading}
        onAdd={() => {
          setAbsencesModalOpen(true);
        }}
        onEdit={(uuid) => {
          setSelectedAbsenceUuid(uuid);
          setAbsencesModalOpen(true);
        }}
        onDelete={async (uuid) => {
          const res = await confirmDelete();
          if (res) handleDelete(uuid);
        }}
        data={data}
      />

      <ClassbookAbsencesModal
        isOpen={absencesModalOpen}
        context={'admin'}
        selectedAbsenceUuid={selectedAbsenceUuid}
        onClose={() => {
          setAbsencesModalOpen(false);
          setSelectedAbsenceUuid(null);
        }}
      />
      <ConfirmationDialog />
    </BpSubpage>
  );
};
